// packages/ui/src/keymap/registerToolContributions.ts
import { useEffect } from 'react';
import type { ToolHost } from '@slate/core';
import { ToolRegistry } from '../tools/ToolRegistry';
import { isMac } from './platform';

interface ParsedCombo {
  mod: boolean;
  shift: boolean;
  alt: boolean;
  key: string;
}

interface BoundCommand {
  combo: ParsedCombo;
  run: () => void;
}

/**
 * Parse a keymap-notation keybinding (`'CmdOrCtrl+Shift+\`'`, `'F6'`) into a
 * normalized combo. `CmdOrCtrl`/`Mod` map to the platform command modifier;
 * single-character keys are lower-cased so matching is case-insensitive. Returns
 * `null` for a spec with no concrete key (e.g. just modifiers).
 */
function parseCombo(spec: string): ParsedCombo | null {
  const parts = spec.split('+').map((s) => s.trim());
  let mod = false;
  let shift = false;
  let alt = false;
  let key = '';
  for (const p of parts) {
    const lp = p.toLowerCase();
    if (lp === 'cmdorctrl' || lp === 'mod') mod = true;
    else if (lp === 'shift') shift = true;
    else if (lp === 'alt' || lp === 'option') alt = true;
    else key = p.length === 1 ? p.toLowerCase() : p;
  }
  if (!key) return null;
  return { mod, shift, alt, key };
}

/**
 * True when a keyboard event matches a parsed combo. The "command" modifier is
 * Meta on macOS, Control elsewhere — and the OTHER platform's modifier must NOT
 * be held, so a Ctrl+key on macOS doesn't double-fire.
 */
function matches(e: KeyboardEvent, c: ParsedCombo): boolean {
  const isModPressed = isMac() ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
  if (c.mod !== isModPressed) return false;
  if (c.shift !== e.shiftKey) return false;
  if (c.alt !== e.altKey) return false;
  const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
  return k === c.key;
}

/**
 * Bind every registered tool's `commands` contribution keybinding at the window
 * level. Rebuilds the binding table whenever the tool registry changes, so a
 * tool registered after mount becomes hot immediately. The first matching combo
 * wins and the event is consumed (preventDefault) so the focused editor never
 * also sees it.
 */
export function useRegisterToolKeybindings(toolHost: ToolHost): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const rebuild = (): BoundCommand[] => {
      const commands = ToolRegistry.contributionsFor('commands');
      const bound: BoundCommand[] = [];
      for (const c of commands) {
        if (!c.keybinding) continue;
        const combo = parseCombo(c.keybinding);
        if (combo) {
          bound.push({
            combo,
            run: () => {
              void c.run(toolHost);
            },
          });
        }
      }
      return bound;
    };
    let bindings = rebuild();
    const unsub = ToolRegistry.subscribe(() => {
      bindings = rebuild();
    });
    const onKey = (e: KeyboardEvent): void => {
      for (const b of bindings) {
        if (matches(e, b.combo)) {
          e.preventDefault();
          b.run();
          return;
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      unsub();
    };
  }, [toolHost]);
}
